import React, { useState, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAccountsStore } from '@/store/accountsStore';
import { useCurrenciesStore } from '@/store/currenciesStore';
import { RefreshCw } from 'lucide-react';
import { Account } from '@/types/base';

interface ExchangeRateInputProps {
  fromAccountId: string;
  toAccountId: string;
  exchangeRate: number;
  onExchangeRateChange: (rate: number) => void;
  onFetchLatestRate?: () => void;
  className?: string;
}

export const ExchangeRateInput: React.FC<ExchangeRateInputProps> = ({
  fromAccountId,
  toAccountId,
  exchangeRate,
  onExchangeRateChange,
  onFetchLatestRate,
  className
}) => {
  const { accounts } = useAccountsStore();
  const { currencies } = useCurrenciesStore();
  const [inputValue, setInputValue] = useState('');

  // Update input value when exchangeRate prop changes
  useEffect(() => {
    if (exchangeRate === 0) {
      setInputValue('');
    } else {
      setInputValue(exchangeRate.toString());
    }
  }, [exchangeRate]);

  const fromAccount = accounts.find(acc => acc.id === fromAccountId);
  const toAccount = accounts.find(acc => acc.id === toAccountId);
  
  // Get currency information from accounts
  const fromAccountCurrencyId = (fromAccount as any)?.currencyId;
  const toAccountCurrencyId = (toAccount as any)?.currencyId;
  const fromAccountCurrencyCode = fromAccount?.currency;
  const toAccountCurrencyCode = toAccount?.currency;
  
  // Try to find currencies by ID or code
  const fromCurrency = currencies.find(curr => 
    curr.id === fromAccountCurrencyId || 
    curr.code === fromAccountCurrencyCode
  );
  
  const toCurrency = currencies.find(curr => 
    curr.id === toAccountCurrencyId || 
    curr.code === toAccountCurrencyCode
  );

  // Check if currencies are different
  const areCurrenciesDifferent = () => {
    // First check using currency objects if available
    if (fromCurrency && toCurrency) {
      // Compare by ID if available
      if (fromCurrency.id && toCurrency.id) {
        return fromCurrency.id !== toCurrency.id;
      }
      
      // Compare by code if available
      if (fromCurrency.code && toCurrency.code) {
        return fromCurrency.code !== toCurrency.code;
      }
    }
    
    // If currency objects aren't available, compare account currency properties directly
    if (fromAccountCurrencyCode && toAccountCurrencyCode) {
      return fromAccountCurrencyCode !== toAccountCurrencyCode;
    }
    
    // If we can't determine, assume they're the same (don't show exchange rate)
    return false;
  };

  // Only show if currencies are different
  const shouldShow = areCurrenciesDifferent();
  
  if (!shouldShow) {
    return null;
  }

  // Determine currency codes for display
  const fromCurrencyCode = fromCurrency?.code || fromAccountCurrencyCode || 'Unknown';
  const toCurrencyCode = toCurrency?.code || toAccountCurrencyCode || 'Unknown';

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <Label htmlFor="exchangeRate" className="text-sm font-medium">
          Exchange Rate (1 {fromCurrencyCode}{(() => {
            const preciousMetals = ['XAU', 'XAG', 'XPT', 'XPD', 'XRH'];
            return preciousMetals.includes(fromCurrencyCode) ? '/g' : '';
          })()} = X {toCurrencyCode}{(() => {
            const preciousMetals = ['XAU', 'XAG', 'XPT', 'XPD', 'XRH'];
            return preciousMetals.includes(toCurrencyCode) ? '/g' : '';
          })()})
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onFetchLatestRate}
          className="flex items-center gap-1 text-xs"
        >
          <RefreshCw className="h-3 w-3" />
          Latest Rate
        </Button>
      </div>
      <Input
        id="exchangeRate"
        type="text"
        value={inputValue}
        onChange={(e) => {
          const value = e.target.value;
          // Allow empty input and any valid decimal number with up to 6 decimal places
          // This regex allows: 0, 0.5, 0.543, 1.234567, etc.
          if (value === '' || /^[0-9]*\.?[0-9]{0,6}$/.test(value)) {
            setInputValue(value);
            if (value === '' || value === '.') {
              onExchangeRateChange(0);
            } else {
              const numValue = parseFloat(value);
              if (!isNaN(numValue) && numValue >= 0) {
                onExchangeRateChange(numValue);
              }
            }
          }
        }}
        onBlur={(e) => {
          const value = e.target.value;
          if (value === '' || value === '.') {
            setInputValue('');
            onExchangeRateChange(0);
          } else {
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue >= 0) {
              // Round to 6 decimal places
              const roundedValue = Math.round(numValue * 1000000) / 1000000;
              setInputValue(roundedValue.toString());
              onExchangeRateChange(roundedValue);
            } else {
              setInputValue('');
              onExchangeRateChange(0);
            }
          }
        }}
        className="mt-1"
        placeholder={`e.g., 0.543 or 1.234567`}
      />
      <p className="text-sm text-gray-500 mt-1">
        Enter the current exchange rate from {fromCurrencyCode}{(() => {
          const preciousMetals = ['XAU', 'XAG', 'XPT', 'XPD', 'XRH'];
          return preciousMetals.includes(fromCurrencyCode) ? '/g' : '';
        })()} to {toCurrencyCode}{(() => {
          const preciousMetals = ['XAU', 'XAG', 'XPT', 'XPD', 'XRH'];
          return preciousMetals.includes(toCurrencyCode) ? '/g' : '';
        })()}
      </p>
    </div>
  );
};

export default ExchangeRateInput; 